import React from 'react';
import { useSurveillanceReportForm } from '../hooks/useSurveillanceReportForm';
import { useUserClientProjects } from '@/shared/hooks/useUserProjects';
import { RESULT_CHOICES } from '../types/types';
import { BaseLayout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

export const CreateSurveillanceReport: React.FC = () => {
  const {
    formData,
    handleChange,
    handleNotificationCheckboxChange,
    handleSubmit,
    isSubmitting,
    error,
    success,
    projectClients,
    loadingClients,
    clientUsers,
    loadingUsers,
  } = useSurveillanceReportForm();

  const { data: projectsData, isLoading: isLoadingProjects } = useUserClientProjects();
  const allProjects = React.useMemo(() => {
    if (!projectsData) return [];
    return [...(projectsData.projects || []), ...(projectsData.invited_projects || [])];
  }, [projectsData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    handleChange('media_files', files);
  };

  return (
    <BaseLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-lg border border-border shadow-sm">
            <div className="p-6">
              <h1 className="text-2xl font-semibold text-foreground mb-6">Create Alert</h1>

            {/* Messages */}
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-6 border-primary/30 bg-primary/10 text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Project Selection */}
              <div className="space-y-2">
                <Label htmlFor="project">Project *</Label>
                {isLoadingProjects ? (
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading projects...</span>
                  </div>
                ) : (
                  <Select
                    value={formData.project}
                    onValueChange={(value) => handleChange('project', value)}
                  >
                    <SelectTrigger id="project" className="w-full">
                      <SelectValue placeholder="Select a project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allProjects.map((project) => (
                        <SelectItem key={project.id} value={String(project.id)}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  type="text"
                  placeholder="Enter alert subject..."
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Result */}
              <div className="space-y-2">
                <Label htmlFor="result">Result *</Label>
                <Select
                  value={formData.result}
                  onValueChange={(value) => handleChange('result', value)}
                >
                  <SelectTrigger id="result" className="w-full">
                    <SelectValue placeholder="Select result..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RESULT_CHOICES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Enter alert description..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={5}
                  className="w-full"
                />
              </div>

              {/* Conclusion */}
              <div className="space-y-2">
                <Label htmlFor="conclusion">Conclusion *</Label>
                <Textarea
                  id="conclusion"
                  placeholder="Enter conclusion..."
                  value={formData.conclusion}
                  onChange={(e) => handleChange('conclusion', e.target.value)}
                  rows={3}
                  className="w-full"
                />
              </div>

              {/* Media Files Upload */}
              <div className="space-y-2">
                <Label htmlFor="media_files">Media Files (Optional)</Label>
                <Input
                  id="media_files"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileChange}
                  className="w-full"
                />
                {formData.media_files && formData.media_files.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {formData.media_files.length} file(s)
                  </p>
                )}
              </div>

              {/* Notification Section */}
              <div className="border-t border-border pt-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="client_notified"
                    checked={formData.client_notified}
                    onCheckedChange={(checked) => 
                      handleNotificationCheckboxChange(checked === true)
                    }
                  />
                  <Label 
                    htmlFor="client_notified" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Client Notified
                  </Label>
                </div>

                {/* Client Selection */}
                {formData.client_notified && (
                  <div className="ml-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="clientSelect">Select Client *</Label>
                      {loadingClients ? (
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Loading clients...</span>
                        </div>
                      ) : (
                        <Select
                          value={formData.clientSelect}
                          onValueChange={(value) => handleChange('clientSelect', value)}
                        >
                          <SelectTrigger id="clientSelect" className="w-full">
                            <SelectValue placeholder="Select a client..." />
                          </SelectTrigger>
                          <SelectContent>
                            {projectClients.map((client) => (
                              <SelectItem key={client.id} value={String(client.id)}>
                                {client.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Notified User Selection */}
                    {formData.clientSelect && (
                      <div className="space-y-2">
                        <Label>Who was notified? *</Label>
                        {loadingUsers ? (
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading users...</span>
                          </div>
                        ) : clientUsers.length > 0 ? (
                          <RadioGroup
                            value={formData.notified_user}
                            onValueChange={(value) => handleChange('notified_user', value)}
                          >
                            {clientUsers.map((user) => (
                              <div key={user.id} className="flex items-center space-x-2">
                                <RadioGroupItem 
                                  value={String(user.id)} 
                                  id={`user_${user.id}`}
                                />
                                <Label 
                                  htmlFor={`user_${user.id}`}
                                  className="font-normal cursor-pointer"
                                >
                                  {user.name}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No users found for this client
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

